import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Shield, Trash2, Ban, XCircle, Eye } from 'lucide-react'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { toast } from '../../../components/Toast'
import {
  Button, Card, Badge, PaginationBar, Modal, ModalHeader, ModalBody, ModalFooter,
} from '../../../components/ui'
import { Thead, Tbody, Tr, Th, Td } from '../../../components/ui/Table'

interface Report {
  id: number
  organization_id: number
  reporter_user_id: number
  reported_user_id: number
  message_id: number | null
  reason: string
  description: string | null
  status: string
  reviewed_by: number | null
  reviewed_at: string | null
  admin_notes: string | null
  created_at: string
  reporter_name: string
  reported_name: string
  message_content: string | null
  message_media_url: string | null
  message_is_deleted: boolean | null
}

const reasonLabels: Record<string, string> = {
  spam: 'Spam',
  harassment: 'Assédio',
  hate_speech: 'Discurso de ódio',
  inappropriate: 'Conteúdo impróprio',
  other: 'Outro',
}

const statusConfig: Record<string, { label: string; variant: string }> = {
  pending: { label: 'Pendente', variant: 'warning' },
  reviewed: { label: 'Revisada', variant: 'info' },
  action_taken: { label: 'Ação tomada', variant: 'success' },
  dismissed: { label: 'Dispensada', variant: 'gray' },
}

export default function CommunityModerationPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [reports, setReports] = useState<Report[]>([])
  const [tab, setTab] = useState('pending')
  const [page, setPage] = useState(1)
  const [meta, setMeta] = useState({ total: 0, last_page: 1, current_page: 1 })
  const [detail, setDetail] = useState<Report | null>(null)

  const fetchReports = useCallback(async () => {
    setLoading(true)
    try {
      const params: any = { page, per_page: 20 }
      if (tab !== 'all') params.status = tab
      const res = await axios.get(buildApiUrl('/v1/community/reports'), { params })
      setReports(res.data.data)
      if (res.data.meta) setMeta(res.data.meta)
    } catch {} finally { setLoading(false) }
  }, [page, tab])

  useEffect(() => { fetchReports() }, [fetchReports])
  useEffect(() => { setPage(1) }, [tab])

  async function reviewReport(id: number, status: string) {
    try {
      await axios.put(buildApiUrl(`/v1/community/reports/${id}/review`), { status })
      toast('success', 'Denúncia atualizada')
      fetchReports()
      setDetail(null)
    } catch { toast('error', 'Erro ao atualizar') }
  }

  async function deleteMessage(messageId: number, reportId: number) {
    try {
      await axios.delete(buildApiUrl(`/v1/community/messages/${messageId}`))
      await axios.put(buildApiUrl(`/v1/community/reports/${reportId}/review`), { status: 'action_taken', admin_notes: 'Mensagem removida' })
      toast('success', 'Mensagem removida')
      fetchReports()
      setDetail(null)
    } catch { toast('error', 'Erro ao remover mensagem') }
  }

  async function banUser(reportId: number, reportedUserId: number) {
    try {
      // Buscar member ID pelo user_id
      const membersRes = await axios.get(buildApiUrl('/v1/community/members'), { params: { per_page: 1, search: '' } })
      const allMembers = membersRes.data.data || []
      const member = allMembers.find((m: any) => m.user_id === reportedUserId)
      if (member) {
        await axios.put(buildApiUrl(`/v1/community/members/${member.id}/status`), { status: 'banned', status_reason: 'Banido via moderação' })
      }
      await axios.put(buildApiUrl(`/v1/community/reports/${reportId}/review`), { status: 'action_taken', admin_notes: 'Usuário banido' })
      toast('success', 'Usuário banido')
      fetchReports()
      setDetail(null)
    } catch { toast('error', 'Erro ao banir') }
  }

  const formatDate = (d: string) => new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })

  const tabs = [
    { key: 'pending', label: 'Pendentes' },
    { key: 'action_taken', label: 'Resolvidas' },
    { key: 'dismissed', label: 'Dispensadas' },
    { key: 'all', label: 'Todas' },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/community/dashboard')} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Moderação</h1>
          <p className="text-gray-500 mt-1">Denúncias e moderação de conteúdo</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              tab === t.key ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tabela */}
      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="w-full">
            <Thead>
              <Tr>
                <Th>Data</Th>
                <Th>Denunciante</Th>
                <Th>Denunciado</Th>
                <Th>Motivo</Th>
                <Th>Mensagem</Th>
                <Th>Status</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <Tr key={i}>
                    {Array.from({ length: 7 }).map((_, j) => (
                      <Td key={j}><div className="h-4 bg-gray-100 rounded animate-pulse" style={{ width: `${50 + Math.random() * 50}%` }} /></Td>
                    ))}
                  </Tr>
                ))
              ) : reports.length === 0 ? (
                <Tr>
                  <Td colSpan={7} className="text-center py-12">
                    <Shield className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Nenhuma denúncia {tab === 'pending' ? 'pendente' : 'encontrada'}</p>
                  </Td>
                </Tr>
              ) : reports.map(r => {
                const sc = statusConfig[r.status] || statusConfig.pending
                return (
                  <Tr key={r.id}>
                    <Td><span className="text-sm text-gray-500 whitespace-nowrap">{formatDate(r.created_at)}</span></Td>
                    <Td><span className="text-sm text-gray-700">{r.reporter_name}</span></Td>
                    <Td><span className="text-sm font-medium text-gray-800">{r.reported_name}</span></Td>
                    <Td><Badge variant={r.reason === 'hate_speech' ? 'error' : r.reason === 'harassment' ? 'warning' : 'gray'}>{reasonLabels[r.reason] || r.reason}</Badge></Td>
                    <Td>
                      <span className="text-sm text-gray-500 truncate block max-w-[200px]">
                        {r.message_content ? (r.message_is_deleted ? <s>{r.message_content}</s> : r.message_content) : '—'}
                      </span>
                    </Td>
                    <Td><Badge variant={sc.variant as any}>{sc.label}</Badge></Td>
                    <Td>
                      <button onClick={() => setDetail(r)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600">
                        <Eye className="w-4 h-4" />
                      </button>
                    </Td>
                  </Tr>
                )
              })}
            </Tbody>
          </table>
        </div>
        <PaginationBar
          page={page}
          perPage={20}
          total={meta.total}
          lastPage={meta.last_page}
          onPageChange={setPage}
          onPerPageChange={() => {}}
        />
      </Card>

      {/* Modal detalhe */}
      {detail && (
        <Modal onClose={() => setDetail(null)} size="md">
          <ModalHeader onClose={() => setDetail(null)}>Denúncia #{detail.id}</ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Denunciante</p>
                  <p className="text-sm font-medium text-gray-800">{detail.reporter_name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Denunciado</p>
                  <p className="text-sm font-medium text-gray-800">{detail.reported_name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Motivo</p>
                  <Badge variant="warning">{reasonLabels[detail.reason] || detail.reason}</Badge>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Data</p>
                  <p className="text-sm text-gray-600">{formatDate(detail.created_at)}</p>
                </div>
              </div>

              {detail.description && (
                <div>
                  <p className="text-xs text-gray-400 mb-1">Descrição</p>
                  <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{detail.description}</p>
                </div>
              )}

              {detail.message_content && (
                <div>
                  <p className="text-xs text-gray-400 mb-1">Mensagem denunciada</p>
                  <div className={`text-sm bg-gray-50 rounded-lg p-3 ${detail.message_is_deleted ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                    {detail.message_content}
                    {detail.message_is_deleted && <span className="text-xs text-error-500 ml-2 no-underline">(removida)</span>}
                  </div>
                </div>
              )}

              {detail.message_media_url && (
                <div>
                  <p className="text-xs text-gray-400 mb-1">Mídia</p>
                  <img src={detail.message_media_url} alt="" className="max-h-48 rounded-lg" />
                </div>
              )}

              {detail.admin_notes && (
                <div>
                  <p className="text-xs text-gray-400 mb-1">Notas do admin</p>
                  <p className="text-sm text-gray-600 bg-blue-50 rounded-lg p-3">{detail.admin_notes}</p>
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            {detail.status === 'pending' && (
              <div className="flex gap-2 w-full">
                {detail.message_id && !detail.message_is_deleted && (
                  <Button size="sm" variant="outline" onClick={() => deleteMessage(detail.message_id!, detail.id)}>
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" />Apagar mensagem
                  </Button>
                )}
                <Button size="sm" variant="danger" onClick={() => banUser(detail.id, detail.reported_user_id)}>
                  <Ban className="w-3.5 h-3.5 mr-1.5" />Banir usuário
                </Button>
                <Button size="sm" variant="outline" onClick={() => reviewReport(detail.id, 'dismissed')}>
                  <XCircle className="w-3.5 h-3.5 mr-1.5" />Dispensar
                </Button>
              </div>
            )}
          </ModalFooter>
        </Modal>
      )}
    </div>
  )
}
